import React, { useState, useRef, useEffect, useContext } from 'react';
import ChatBotConversation from './ChatBotConversation.jsx';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ChatBotContext } from '../../context/ChatBotContext.js';
import { getChatGptResponse } from '../services/chatService.js';
import { useAuthStore } from '../../store/store.js';
import { Survey } from '../../data/chatbotSurvey.js';
import {
    getFullName,
    getAvatarMetaData,
    getAvatarCreationStatus,
} from '../../api/user.js';
import {
    getDailyCheckInStatus,
    updateDailyCheckIn,
    updateDailyCheckInStatus,
    updateBadges,
} from '../../api/checkin';

const WELCOME_MSG = 'To get started today, I need to ask you a few questions about how you are doing.';
const CONGRATS_MSG = 'Congratulations! You have earned a New Badge: ';

const ChatBotWindow = () => {
    const uid = useAuthStore(store => store.user?.uid);
    const isAuth = useAuthStore(store => store.isAuth);
    const chatContainerRef = useRef(null);
    const { chatPrompt, setChatPrompt, userPrompt, setUserPrompt } = useContext(ChatBotContext);
    const [inputValue, setInputValue] = useState('');
    const [conversation, setConversation] = useState([]);
    const lockedQuestionsRef = useRef(new Set());
    const isQueryEnabled = !(uid === undefined || uid === null);
    const [surveyActive, setSurveyActive] = useState(false);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [chatReady, setChatReady] = useState(false);
    const answersRef = useRef({});
    const surveyStartedRef = useRef(false);

    const [userData, setUserData] = useState({
        fullName: 'Anonymous',
        userAvatarColors: null,
        userAvatarStyles: null,
        avStatus: false,
    });

    // Queries and their Mutation Functions.
    const userFullNameQuery = useQuery({
        queryKey: ['fullName', uid],
        queryFn: () => getFullName(uid),
        enabled: isQueryEnabled,
    });

    const avatarQuery = useQuery({
        queryKey: ['avatar', uid],
        queryFn: () => getAvatarMetaData(uid),
        enabled: isQueryEnabled,
    });

    const avatarCreationStatus = useQuery({
        queryKey: ['created_avatar', uid],
        queryFn: () => getAvatarCreationStatus(uid),
        enabled: isQueryEnabled,
    });

    const dailyCheckInStatusQuery = useQuery({
        queryKey: ['dailyCheckInStatus', uid],
        queryFn: () => getDailyCheckInStatus(uid),
        enabled: !(uid === null || uid === undefined),
    });

    const updateDailyCheckInMutation = useMutation(updateDailyCheckIn);
    const updateDailyCheckInStatusMutation = useMutation(updateDailyCheckInStatus);

    const addDailyCheckInResults = async() => {
        updateDailyCheckInMutation.mutate({ uid, dailyCheckInSurvey: { ...answersRef.current } });
    };
    const updateCheckInStatus = () => {
        updateDailyCheckInStatusMutation.mutate({ uid });
    };

    useEffect(() => {
        if (!isAuth) return;
        if (!userFullNameQuery.data) return;
        if (!avatarCreationStatus.data) return;
        if (!avatarQuery.data) return;
        if (chatReady) return;

        setUserData({
            fullName: userFullNameQuery.data,
            userAvatarColors: avatarQuery.data?.avatarColors ?? null,
            userAvatarStyles: avatarQuery.data?.avatarStyles ?? null,
            avStatus: avatarCreationStatus.data ?? false,
        });

        setConversation([
            {
                bot: (
                    <p>
                        Hello <b>{userFullNameQuery.data}</b>, I&apos;m your Wellness Buddy!
                    </p>
                ),
            },
        ]);

        setChatReady(true);
    }, [
        isAuth,
        userFullNameQuery.data,
        avatarQuery.data,
        avatarCreationStatus.data,
        chatReady,
    ]);

    useEffect(() => {
        if (!chatReady) return;
        if (!dailyCheckInStatusQuery.isSuccess) return;
        if (dailyCheckInStatusQuery.data !== false) return;
        if (surveyActive) return;
        if (surveyStartedRef.current) return;

        setSurveyActive(true);
        pushBotMessage(WELCOME_MSG);
        pushBotMessage(renderSurveyQuestion(0));
    }, [
        chatReady,
        dailyCheckInStatusQuery.data,
        dailyCheckInStatusQuery.isSuccess,
        surveyActive,
    ]);

    useEffect(() => {
        if (!chatPrompt || !userPrompt) return;
        if (!dailyCheckInStatusQuery.data) return; // survey must be complete

        const respond = async() => {
            addToConversation(userPrompt);
            const botResp = await getChatGptResponse(chatPrompt, null, null);
            pushBotMessage(botResp);
            setChatPrompt('');
            setUserPrompt('');
        };

        respond();
    }, [chatPrompt, userPrompt, dailyCheckInStatusQuery.data]);

    const scrollToBottom = () => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop =
                chatContainerRef.current.scrollHeight -
                chatContainerRef.current.clientHeight;
            setTimeout(() => {
                chatContainerRef.current.scrollTop =
                    chatContainerRef.current.scrollHeight - chatContainerRef.current.clientHeight;
            }, 300);
        } else {
            // Shouldn't reach here, unless error occured.
            // TODO: Logging of some sort on the production build.
        }
    };

    const addToConversation = (userInput) => {
        if (userInput !== '') {
            setConversation((prevConvo) => [...prevConvo, { user: userInput }]);
        }
        scrollToBottom();
    };

    const pushBotMessage = (msg) => {
        setConversation(prev => [...prev, { bot: msg }]);
        scrollToBottom();
    };

    const finishSurvey = async() => {
        setSurveyActive(false);
        surveyStartedRef.current = true;

        try {
            let botResp = await getChatGptResponse('', null, answersRef.current);
            const badgeFound = await updateBadges({ uid });

            if (badgeFound.length > 0) {
                botResp = (
                    <>
                        {botResp}
                        <br />
                        <p className="font-bold">
                            {CONGRATS_MSG}
                            <span className="text-red italic underline">
                                {badgeFound[0]}!
                            </span>
                        </p>
                    </>
                );
            }

            pushBotMessage(botResp);
            addDailyCheckInResults();
            updateCheckInStatus();
            scrollToBottom();
            pushBotMessage(
                'Would you like to talk about it some more? You can tell me how things are going.',
            );
        } catch {
            pushBotMessage('Sorry! Something went wrong.');
        } finally {
            setQuestionIndex(0);
            answersRef.current = {};
        }
    };

    const handleSurveyAnswer = (title, value, category, index) => {
        if (lockedQuestionsRef.current.has(index)) return;
        lockedQuestionsRef.current.add(index);
        answersRef.current[title] = value;

        // show user choice
        setConversation(prev => [...prev, { user: category }]);

        const nextIndex = index + 1;

        if (nextIndex < Survey.questions.length) {
            setQuestionIndex(nextIndex);
            pushBotMessage(renderSurveyQuestion(nextIndex));
        } else {
            finishSurvey();
        }
    };

    const renderSurveyQuestion = (index) => {
        const question = Survey.questions[index];
        const isLocked = lockedQuestionsRef.current.has(index);
        return (
            <div className={isLocked ? 'opacity-60 pointer-events-none' : ''}>
                <p>{question.question}</p>

                <div className="flex items-center flex-row xl:gap-16 lg:gap-14 md:gap-12 sm:gap-8 xs:gap-2 p-2">
                    {Survey.values[index].map((choice, i) => (
                        <label
                            key={i}
                            className={`flex flex-col items-center gap-2 ${
                                isLocked ? 'cursor-not-allowed' : 'cursor-pointer'
                            }`}
                        >
                            <img
                                src={choice.icon}
                                alt={choice.category}
                                className={`border-2 rounded-lg
                                w-10 h-10 lg:w-14 lg:h-14
                                md:w-12 md:h-12 xl:w-16 xl:h-16
                                ${isLocked ? 'opacity-50' : ''}`}
                            />
                            <input type='radio' name={`question-${index}`} value={choice.value} className='bg-gray-200' disabled={isLocked} onClick={() => handleSurveyAnswer(question.title, choice.value, choice.category, index)} />
                        </label>
                    ))}
                </div>
            </div>
        );
    };

    const handleSubmit = async(event) => {
        event.preventDefault();
        if (surveyActive) return;

        const userInput = inputValue.trim();
        if (!userInput) return;

        addToConversation(userInput);
        setInputValue('');
        const botResp = await getChatGptResponse(userInput, conversation, null);
        pushBotMessage(botResp);
    };

    return (
        <>
            <div id="container-window flex flex-col justify-start items-right w-full" className="container-window">
                <div className="chat-window flex flex-col justify-center items-center overflow-hidden break-words h-80 w-full">
                    <div
                        className="messages-window bg-chatbotGrey overflow-y-auto w-100 h-100"
                        style={{
                            color: 'black',
                        }}
                        ref={chatContainerRef}
                    >
                        <ChatBotConversation conversation={conversation} currIndex={questionIndex} userData={userData}/>
                    </div>
                    <form className="messages-input w-full" onSubmit={handleSubmit}>
                        <input
                            id="input"
                            className="input-chatbot w-100 p-3 rounded-b-2xl text-base bg-white focus:border-solid-border-1-border-sky_blue outline-none .placeholder-gray-500::placeholder border-#ccc"
                            name="inputField"
                            type="text"
                            placeholder="Say something..."
                            autoComplete="off"
                            value={inputValue}
                            onChange={(event) => { setInputValue(event.target.value); }}
                            disabled={surveyActive}
                        />
                    </form>
                </div>
            </div>
        </>
    );
};

export default ChatBotWindow;
